/**
 * Retirement Village suggestion engine — generate & rank a curated set of funding/structuring strategies
 * for a chosen village (entry contribution + ongoing fees + exit terms are GIVEN; we optimise how to fund
 * and structure around them). Mirrors the Aged Care / Support at Home goal-leader pattern: each candidate is
 * evaluated with the same RV projection engine the manual path uses, then ranked into Best legacy / Best
 * cashflow / Best Age Pension via the shared `buildRecommendedOptimalStrategies`.
 *
 * A curated BASE set, then composable dimensions — the same shape as the aged care engine. The base
 * strategies are clearly-named, explainable answers to "how do we fund and structure this move" (only
 * those applicable to the client are generated: home present, investment property present, downsizer
 * age eligibility). Orthogonal decisions that combine with any of them — currently debt clearance —
 * are then PAIRED against every base move strategy rather than listed once, so "sell the home, start
 * a super pension AND clear the car loan" is a ranked candidate, not something the adviser has to
 * assemble by hand. The full universe and its exclusions are documented for actuarial review in
 * docs/actuarial-review/C_STRATEGIES.md — change one, change the other.
 */
import { villageInterestHeld, villageInterestToday } from '../../../shared/src/calculation/support/villageExit.ts';
import type { DraftFactFind } from '../constants/initialFactFind.ts';
import { annuityPreferenceOf } from '../../../shared/src/calculation/annuityPreference.ts';
import { guaranteedShapeAvailable } from '../../../shared/src/calculation/carePlusSplit.ts';
import { buildRecommendedOptimalStrategies, type EvaluatedStrategy, type RecommendedStrategyEntry } from './supportAtHomeRecommendedStrategies.ts';
import { sumAssetType, type FundingMap } from './fundingMechanisms.ts';
import { DOWNSIZER_CONTRIBUTION_CAP } from '../constants/index.ts';
import { calculateAge, formatCurrency } from '../utils/index.ts';
import { FUNERAL_BOND_LIMIT, GIFTING_LIMIT_ANNUAL } from '../../../shared/src/calculation/constants/ratesAndThresholds.ts';
import { getRecommendedEmergencyFund } from '../utils/emergencyFund.ts';
// One debt rulebook across modules: the same helper the aged care engine pairs its candidates with
// (it knows reverse mortgages cannot be paid out early, and what each debt is secured against).
import { payableDebtsOf } from '../features/agedCare/DebtPayoffStrategy.tsx';
import { ASSET_CLASS_ASSUMPTIONS } from '../../../shared/src/calculation/investmentProfiles.ts';
import { purchaseWithinBudget, isStampDutyState } from '../../../shared/src/calculation/stampDuty.ts';
import { allocateDownsizerContribution } from '../../../shared/src/calculation/downsizerContributionUtils.ts';
import type { FactFind } from '../../../shared/src/calculation/factFindTypes.ts';
import * as calculationApi from '../services/calculationApi.ts';

export interface RetirementVillageInputs {
    entryFee?: number;
    ongoingFees?: number;
    [key: string]: unknown;
}

export interface RvSellHomeDetails {
    salePrice: number;
    sellingCosts: number;
    downsizerContribution: number;
    partnerDownsizerContribution: number;
    commenceIncomeStream: boolean;
    giftingAmount: number;
    funeralBondPurchase: number;
    purchaseAnnuity?: number;
    purchaseInvestmentProperty?: number;
    purchaseShares?: number;
    investmentPropertyRent?: number;
    investmentPropertyExpenses?: number;
    /** The state a bought investment property is in, and the duty on buying it (stampDuty.ts). Absent: no duty modelled. */
    investmentPropertyState?: string;
    investmentPropertyStampDuty?: number;
    rvAnnuityType?: string;
    /** Share of the purchase price promised back on death, 0-100, before the capital access schedule winds it down. Both ends are offered — see above. */
    rvAnnuityResidualPercent?: number;
    /** A provider's quoted annual income, replacing the tool's indicative rate for this candidate. */
    rvAnnuityIncomeOverride?: number;
    [key: string]: unknown;
}

export interface RvRentDetails {
    rentAmount: number;
    rentExpenses: number;
    partnerRent: number;
}

export interface RvInvestmentPropertyDetails {
    selectedPropertyId: string | number | null;
    entryFeeAllocation: number;
    downsizerContribution: number;
    partnerDownsizerContribution: number;
    commenceIncomeStream: boolean;
    giftingAmount: number;
    funeralBondPurchase: number;
}

/** A rounded dollar figure for a strategy NAME — "$1.5m", "$430k" — so a card says how big the
 *  commitment is without a nine-character number in its title. */
function formatRoughAmount(amount: number): string {
    if (amount >= 1_000_000) return `$${(amount / 1_000_000).toFixed(amount >= 10_000_000 ? 0 : 1).replace(/\.0$/, '')}m`;
    if (amount >= 1_000) return `$${Math.round(amount / 1_000)}k`;
    return `$${Math.round(amount)}`;
}

export interface RetirementVillageCandidate {
    id: string;
    name: string;
    description: string;
    fundFrom: 'liquid' | 'sell_home' | 'investment_property';
    /** Debts cleared as part of this strategy — forwarded to the engine's debt payoff plugin. */
    debtPayoff?: { payments?: Record<string, number>; funding?: Record<string, number>; repaymentBasis?: 'keep_repayment' | 'lower_repayment' };
    entryFeeFundingOverride?: FundingMap;
    rentHome?: boolean;
    rentDetails?: RvRentDetails;
    sellHomeDetails?: RvSellHomeDetails;
    investmentPropertyDetails?: RvInvestmentPropertyDetails;
    // evaluateRetirementVillageCandidates (calculationApi.ts) takes Record<string, unknown>[] — this
    // index signature lets a typed RetirementVillageCandidate[] satisfy that without a call-site cast.
    [key: string]: unknown;
}

/**
 * The Strategy Analysis Setup answers (owner, 24 Sep 2026) — the choices the strategies turn on
 * that are the client's to make, asked before the run and saved on the client record (goals.rv*).
 */
/** "Would you consider locking part of the money away for a guaranteed income for life?" */
export type RvAnnuityAppetite = 'none' | 'yes' | 'unsure';
/** "At the end, should the money come back to the family?" — which residual versions to build. */
export type RvAnnuityResidual = 'returned' | 'nil' | 'guaranteed' | 'both';

export interface RvEngineSetup {
    /** The state a bought investment property would be in; blank = no duty modelled, said on the card. */
    purchaseState?: string | null;
    /** The most of the freed-up money a lifetime annuity may take, 0–100. Default a quarter. */
    annuityShareCapPct?: number | null;
    /** Whether an annuity may be offered at all. 'none' builds no annuity candidate. Default 'unsure' (both versions). */
    annuityAppetite?: RvAnnuityAppetite | null;
    /** Which residual versions to build when the answer was 'yes'. Default both. */
    annuityResidual?: RvAnnuityResidual | null;
    /**
     * Moving from another village (8 Oct 2026): the current unit's payout is taken as paid at the
     * move — funding the new contribution first — unless the adviser says it comes on the state's
     * timeline and is bridged from savings. Default true.
     */
    oldUnitPaysAtMove?: boolean | null;
    /** The yearly surplus a plan must keep for the cashflow-covered goal. Zero = the bills are paid. */
    cashflowTarget?: number | null;
}

/**
 * A quarter, by default. Committing every spare dollar to a lifetime annuity is a position few
 * advisers would take, and "all in" kept winning the cashflow goal on the strength of the income
 * alone; a visible, changeable cap lets the field model the shape a real recommendation has — a
 * guaranteed floor with accessible capital beside it — and the adviser can still say 100.
 */
export const DEFAULT_RV_ANNUITY_SHARE_CAP_PCT = 25;

export function annuityShareCapOf(setup: RvEngineSetup | null | undefined): number {
    const raw = Number(setup?.annuityShareCapPct);
    if (!Number.isFinite(raw) || setup?.annuityShareCapPct == null) return DEFAULT_RV_ANNUITY_SHARE_CAP_PCT;
    return Math.min(100, Math.max(0, raw));
}

/** The saved answers, with the village overview's state as the fallback for the property's. */
export function rvEngineSetupFromGoals(factFind: DraftFactFind | null | undefined, fallbackState?: string | null): RvEngineSetup {
    const goals = (factFind?.goals || {}) as { rvPurchaseState?: string; rvAnnuityShareCapPct?: number; rvCashflowTarget?: number; rvOldUnitPaysAtMove?: boolean };
    const saved = typeof goals.rvPurchaseState === 'string' && goals.rvPurchaseState.trim() !== '' ? goals.rvPurchaseState : null;
    // The willingness questions are the CLIENT's, shared with every other module (annuityPreference.ts).
    const preference = annuityPreferenceOf(factFind as never);
    return {
        purchaseState: saved ?? (fallbackState || ''),
        annuityShareCapPct: goals.rvAnnuityShareCapPct != null && Number.isFinite(Number(goals.rvAnnuityShareCapPct)) ? Number(goals.rvAnnuityShareCapPct) : DEFAULT_RV_ANNUITY_SHARE_CAP_PCT,
        annuityAppetite: preference.appetite,
        annuityResidual: preference.residual,
        cashflowTarget: Number(goals.rvCashflowTarget) > 0 ? Number(goals.rvCashflowTarget) : 0,
        oldUnitPaysAtMove: typeof goals.rvOldUnitPaysAtMove === 'boolean' ? goals.rvOldUnitPaysAtMove : true,
    };
}

export interface RetirementVillageSuggestionsOptions {
    /** The case village entry date, threaded to the server so every candidate shares the manual run timing. */
    villageEntryDate?: string;
    legacyYears?: number;
    /** The Strategy Analysis Setup answers. Absent: the defaults (no state, a quarter, bills paid). */
    setup?: RvEngineSetup;
    /**
     * A real annuity quote, replacing the tool's indicative rate on every candidate that buys one
     * (owner, 2026-08-26: "it would be good to allow the user to alter the income estimate and have
     * the system remodel on this same scenario with the quote").
     *
     * The engine has always accepted `rvAnnuityIncomeOverride`; this simply lets the adviser reach
     * it from the Suggestion Engine, so a provider's actual figure re-ranks the field instead of
     * sitting beside numbers built on a different income. Applied to the annuity candidates only —
     * nothing else in the field has an annuity income to override.
     */
    annuityIncomeOverride?: number | null;
}

export interface RetirementVillageSuggestionsResult {
    strategies: EvaluatedStrategy[];
    recommended: RecommendedStrategyEntry[];
    /** The stay-home (do nothing) evaluation — the benchmark, rendered as its own baseline card. */
    stayHome: EvaluatedStrategy | null;
}

/**
 * Default entry-fee funding from liquid assets (bank → term deposits → account-based pensions → shares),
 * keeping the recommended emergency reserve (6 months of living expenses) in the bank. Mirrors the manual-control
 * default allocation so the suggestion engine and manual control agree.
 */
function buildDefaultEntryFeeAllocation(
    factFind: DraftFactFind,
    entryFee: number,
    order: string[] = ['bankAccounts', 'termDeposits', 'accountBasedPensions', 'shares'],
): FundingMap {
    if (!(entryFee > 0) || !factFind?.assets) return {};
    const reserve = getRecommendedEmergencyFund(factFind);
    let remaining = entryFee;
    const out: FundingMap = {};
    order.forEach((type) => {
        if (remaining <= 0 || !factFind.assets?.[type]) return;
        if (type === 'bankAccounts') {
            const bankAccounts = factFind.assets.bankAccounts || [];
            const totalBank = bankAccounts.reduce((s, a) => s + (a.value || 0), 0);
            const availableFromBank = Math.max(0, totalBank - reserve);
            let takenFromBank = 0;
            bankAccounts.forEach((asset) => {
                if (remaining <= 0 || takenFromBank >= availableFromBank) return;
                const val = asset.value || 0;
                const canTake = Math.min(val, remaining, availableFromBank - takenFromBank);
                if (canTake > 0) {
                    out[`bankAccounts-${asset.id}`] = canTake;
                    remaining -= canTake;
                    takenFromBank += canTake;
                }
            });
            return;
        }
        (factFind.assets[type] || []).forEach((asset) => {
            if (remaining <= 0) return;
            const val = asset.value || 0;
            const take = Math.min(val, remaining);
            if (take > 0) {
                out[`${type}-${asset.id}`] = take;
                remaining -= take;
            }
        });
    });
    return out;
}

/**
 * The curated candidate strategies for this client + village (input configs only; evaluation happens below).
 */
export function generateRetirementVillageCandidates(factFind: DraftFactFind, villageInputs: RetirementVillageInputs, setup: RvEngineSetup = {}): RetirementVillageCandidate[] {
    const entryFee = villageInputs?.entryFee || 0;
    /*
     * MOVING FROM ANOTHER VILLAGE (owner, 8 Oct 2026). Taken as paid at the move, the current unit's
     * payout funds the new contribution first: the funding test and the savings allocation below
     * are for the REST. Without this a $1,000,000 entry against $580,000 of savings produced no
     * candidate at all, though $520,000 was coming back from the old unit (owner-caught).
     */
    const payoutAtMove = setup?.oldUnitPaysAtMove !== false && villageInterestHeld(factFind as unknown as FactFind) ? villageInterestToday(factFind as unknown as FactFind) : 0;
    const entryFromLiquid = Math.max(0, entryFee - payoutAtMove);
    /*
     * The plain-words annuity answers: "no" builds no annuity candidate at all; "yes" may narrow
     * the field to one residual version; "not sure" (the default) shows both at the cap.
     *
     * The SETUP's answer wins, and where it is silent the CLIENT RECORD is read — the same answer
     * every other module reads (annuityPreference.ts). Without that fallback a caller who did not
     * thread the setup through would have ignored a client who had said no (caught by
     * annuityPreference.test.ts, 25 Sep 2026).
     */
    const saved = annuityPreferenceOf(factFind as never);
    const annuityAppetite: RvAnnuityAppetite = setup?.annuityAppetite === 'none' || setup?.annuityAppetite === 'yes' || setup?.annuityAppetite === 'unsure'
        ? setup.annuityAppetite
        : saved.appetite;
    const annuityCapPct = annuityAppetite === 'none' ? 0 : annuityShareCapOf(setup);
    const residualAnswer = setup?.annuityResidual === 'returned' || setup?.annuityResidual === 'nil' || setup?.annuityResidual === 'guaranteed' || setup?.annuityResidual === 'both'
        ? setup.annuityResidual
        : saved.residual;
    const annuityResidual: RvAnnuityResidual = annuityAppetite === 'yes' && (residualAnswer === 'returned' || residualAnswer === 'nil' || residualAnswer === 'guaranteed') ? residualAnswer : 'both';
    // The guaranteed shape is sold only to someone in, or entering, government-subsidised care.
    const offerGuaranteed = (annuityResidual === 'guaranteed' || annuityResidual === 'both') && guaranteedShapeAvailable(factFind as never);
    const purchaseState = isStampDutyState(setup?.purchaseState) ? setup.purchaseState : undefined;
    /** The property a budget buys once its stamp duty is paid, with the rent struck on the PRICE. */
    const propertyFor = (budget: number) => {
        const bought = purchaseWithinBudget(purchaseState, budget);
        const rent = Math.round(bought.price * (ASSET_CLASS_ASSUMPTIONS.propertyYieldPct / 100));
        return {
            purchaseInvestmentProperty: bought.price,
            investmentPropertyStampDuty: bought.duty,
            investmentPropertyState: purchaseState,
            investmentPropertyRent: rent,
            investmentPropertyExpenses: Math.round(rent * (ASSET_CLASS_ASSUMPTIONS.propertyExpenseRatioPct / 100)),
        };
    };
    const dutyWords = (duty: number) => (purchaseState
        ? ` Stamp duty of ${formatCurrency(duty)} (${purchaseState}) comes out of the proceeds, so the property is sized to what is left.`
        : ' Stamp duty is not modelled — no state was chosen for the purchase.');
    const hasHome = (factFind?.home?.value || 0) > 0;
    const homeValue = factFind?.home?.value || 0;
    const investmentProps = factFind?.assets?.investmentProperty || [];
    const hasIp = investmentProps.length > 0;
    const isCouple = factFind?.adviceFor === 'couple';
    const clientAge = factFind?.client?.dob ? calculateAge(factFind.client.dob) : null;
    const partnerAge = isCouple && factFind?.partner?.dob ? calculateAge(factFind.partner.dob) : null;
    const downsizerEligible = (clientAge != null && clientAge >= 55) || (partnerAge != null && partnerAge >= 55);

    const candidates: RetirementVillageCandidate[] = [];

    // 0. Stay in the current home — the do-nothing baseline every move strategy is measured
    //    against (the same benchmark discipline as the aged care and Support at Home engines).
    //    Evaluated with the village zeroed out: no entry fee, no ongoing fees, no move.
    candidates.push({
        id: 'rv-stay-home',
        name: 'Stay in your current home',
        description: 'No village move — life as it is today. This is the baseline every strategy below is measured against.',
        fundFrom: 'liquid',
        stayHome: true,
    });

    // 1. Fund from savings (keep home as-is). Always available. When the entry fee needs more than cash AND the
    //    client holds both shares and an account-based pension, COMPARE drawing the tax-free ABP (no CGT) vs
    //    selling shares (CGT) — the projected after-CGT / after-return outcome decides which is the goal leader.
    const cashAvail = Math.max(0, sumAssetType(factFind, 'bankAccounts') - getRecommendedEmergencyFund(factFind)) + sumAssetType(factFind, 'termDeposits');
    const hasShares = sumAssetType(factFind, 'shares') > 0;
    const hasAbp = sumAssetType(factFind, 'accountBasedPensions') > 0;
    const compareLiquid = cashAvail < entryFee && hasShares && hasAbp;
    const liquidVariants = compareLiquid
        ? [
            { key: '-abp', nameSuffix: ' (drawing your account-based pension)', order: ['bankAccounts', 'termDeposits', 'accountBasedPensions', 'shares'], desc: 'draw your tax-free account-based pension before shares — no CGT' },
            { key: '-shares', nameSuffix: ' (selling shares)', order: ['bankAccounts', 'termDeposits', 'shares', 'accountBasedPensions'], desc: 'sell shares (realising CGT) and preserve your account-based pension' },
        ]
        : [{ key: '', nameSuffix: '', order: ['bankAccounts', 'termDeposits', 'accountBasedPensions', 'shares'], desc: 'bank, term deposits, account-based pension, then shares' }];
    /*
     * A village entry contribution CANNOT BE PART-PAID (owner-caught, 2026-08-26). It is a
     * contractual price for the right to occupy, not a statutory payment like a residential aged
     * care room — there is no village equivalent of choosing a RAD, a DAP, or a combination of the
     * two, and no operator lets a resident pay a fraction and move in anyway.
     *
     * So a funding path that cannot raise the whole contribution is not a strategy; it is a plan
     * that fails. Offering one was doing real damage, because the engine used to draw whatever the
     * balance sheet held and STILL book the exit entitlement on the full contracted price: an
     * $800,000 contribution funded from $300,000 of assets manufactured about $500,000 of net worth,
     * and the ranking then promoted that candidate to "best legacy" on the strength of the phantom.
     * The engine side is now fixed too (VillageContract.entryFeePaid), but the honest fix is here —
     * a strategy the client cannot actually execute should never be generated.
     *
     * Measured against what the funding would ACTUALLY raise, by summing the same allocation the
     * candidate would carry, so the test and the strategy can never disagree.
     */
    const fundedByLiquid = (order: string[]) =>
        Object.values(buildDefaultEntryFeeAllocation(factFind, entryFromLiquid, order)).reduce((sum, amount) => sum + (amount || 0), 0);
    const canFundFromLiquid = liquidVariants.some((v) => fundedByLiquid(v.order) >= entryFromLiquid - 1);

    if (canFundFromLiquid) {
        liquidVariants.forEach((v) => {
            if (fundedByLiquid(v.order) < entryFromLiquid - 1) return;
            candidates.push({
                id: `rv-liquid${v.key}`,
                name: `Fund from your savings${v.nameSuffix}`,
                description: `Pay the entry contribution from your savings (${v.desc}), and keep your home as it is.`,
                fundFrom: 'liquid',
                entryFeeFundingOverride: buildDefaultEntryFeeAllocation(factFind, entryFromLiquid, v.order),
            });
        });
    }

    if (hasHome) {
        // 2. Keep and rent the former home (income to cashflow; entry still funded from savings).
        //    Keeping the home means the entry contribution still has to come out of savings, so this
        //    is subject to the same all-or-nothing funding test as the plain savings candidates —
        //    this is the exact strategy that was topping the rankings on a $500k phantom.
        if (canFundFromLiquid) {
            candidates.push({
                id: 'rv-liquid-rent',
                name: 'Keep and rent your home',
                description: 'Pay the entry contribution from your savings and rent out your former home for extra income.',
                fundFrom: 'liquid',
                rentHome: true,
                rentDetails: {
                    rentAmount: factFind?.home?.likelyRent || 0,
                    rentExpenses: factFind?.home?.likelyExpenses || 0,
                    partnerRent: 0,
                },
            });
        }

        // 3. Sell the home to fund the move.
        const sellingCosts = Math.round(homeValue * 0.025);
        candidates.push({
            id: 'rv-sell-home',
            name: 'Sell your home',
            description: 'Sell the family home, use the proceeds for the entry contribution, and invest the rest.',
            fundFrom: 'sell_home',
            sellHomeDetails: {
                salePrice: homeValue,
                sellingCosts,
                downsizerContribution: 0,
                partnerDownsizerContribution: 0,
                commenceIncomeStream: false,
                giftingAmount: 0,
                funeralBondPurchase: 0,
                purchaseAnnuity: 0,
                purchaseInvestmentProperty: 0,
                purchaseShares: 0,
            },
        });

        const netAfterEntrySell = Math.max(0, homeValue - sellingCosts - entryFee);

        // 4. Sell the home, pay the entry fee, and invest the rest in shares / managed funds.
        if (netAfterEntrySell >= 50000) {
            candidates.push({
                id: 'rv-sell-home-invest',
                // Named for WHAT it buys. "Invest the rest" left an adviser asking "invest in what?",
                // which is the wrong question to be asking of a recommendation.
                name: 'Sell your home and invest in shares / managed funds',
                description: 'Sell the home, pay the entry contribution, and put the freed-up money into a diversified share / managed-fund portfolio instead of the bank — aiming for higher long-term growth and income (still counted for the pension; returns aren\'t guaranteed).',
                fundFrom: 'sell_home',
                sellHomeDetails: {
                    salePrice: homeValue, sellingCosts,
                    downsizerContribution: 0, partnerDownsizerContribution: 0,
                    commenceIncomeStream: false, giftingAmount: 0,
                    funeralBondPurchase: 0, purchaseAnnuity: 0,
                    purchaseInvestmentProperty: 0, purchaseShares: netAfterEntrySell,
                },
            });

            // 5. Sell the home, pay the entry fee, and buy an investment property for rental income.
            //    Yield and expense ratio from the published asset-class assumptions (a licensee's
            //    own figures where their firm has set them) — see investmentProfiles.ts. The stamp
            //    duty on the purchase comes out of the proceeds where a state was chosen (owner,
            //    24 Sep 2026); the property is what is left, and the rent is struck on that.
            const rvIp = propertyFor(netAfterEntrySell);
            candidates.push({
                id: 'rv-sell-home-property',
                name: 'Sell your home and buy an investment property',
                description: 'Sell the home, pay the entry contribution, and use the freed-up money to buy an investment property for rental income and potential growth. The property is an assessable asset and the net rent counts for the pension; it\'s less flexible than shares and has buying/selling costs.' + dutyWords(rvIp.investmentPropertyStampDuty),
                fundFrom: 'sell_home',
                sellHomeDetails: {
                    salePrice: homeValue, sellingCosts,
                    downsizerContribution: 0, partnerDownsizerContribution: 0,
                    commenceIncomeStream: false, giftingAmount: 0,
                    funeralBondPurchase: 0, purchaseAnnuity: 0, purchaseShares: 0,
                    ...rvIp,
                },
            });

            // 6. Sell the home, pay the entry fee, and buy a lifetime annuity for guaranteed income —
            //    sized to the share of the freed-up money the setup allows (a quarter by default,
            //    changeable up to all of it), with the rest invested in shares / managed funds so
            //    it stays accessible. Committing every spare dollar to a lifetime annuity is a
            //    position few advisers would take, and modelled as the only size it kept winning
            //    the cashflow goal on the income alone (owner, 24 Sep 2026). Under $25,000 is too
            //    small to be worth modelling.
            const annuityAmount = Math.round(netAfterEntrySell * (annuityCapPct / 100));
            if (annuityAmount >= 25000) {
                const annuityRest = Math.max(0, netAfterEntrySell - annuityAmount);
                const sizeWords = annuityCapPct >= 100
                    ? `all ${formatRoughAmount(annuityAmount)}`
                    : `${formatRoughAmount(annuityAmount)}, ${Math.round(annuityCapPct)}% of what's freed up`;
                const restWords = annuityRest > 0 ? ' and invest the rest' : '';
                if (annuityResidual !== 'returned' && annuityResidual !== 'guaranteed') candidates.push({
                    id: 'rv-sell-home-annuity',
                    name: `Sell your home, buy a lifetime annuity (${sizeWords})${restWords}`,
                    description: `Sell the home, pay the entry contribution, and convert ${annuityCapPct >= 100 ? 'all' : `${Math.round(annuityCapPct)}%`} of the freed-up money into a lifetime annuity — guaranteed income for life, and only 60% of the purchase price counted in the assets test${annuityRest > 0 ? ' — with the rest invested in shares / managed funds where it stays accessible' : ''}. Nil residual: the income is higher, and nothing of the purchase price comes back to the estate.`,
                    fundFrom: 'sell_home',
                    sellHomeDetails: {
                        salePrice: homeValue, sellingCosts,
                        downsizerContribution: 0, partnerDownsizerContribution: 0,
                        commenceIncomeStream: false, giftingAmount: 0,
                        funeralBondPurchase: 0, purchaseInvestmentProperty: 0,
                        purchaseShares: annuityRest,
                        purchaseAnnuity: annuityAmount, rvAnnuityType: 'lifetime',
                        rvAnnuityResidualPercent: 0,
                    },
                });

                /*
                 * THE SAME ANNUITY WITH THE CAPITAL RETURNED — offering only the nil-residual version
                 * disqualified the strategy rather than weighing it.
                 *
                 * Centrelink assesses a lifetime annuity at 60% of its purchase price WHATEVER its
                 * residual capital value: RCV changes the payment rate, not the assets-test treatment.
                 * But net worth values an annuity at its RESIDUAL (assessment/netWorth.ts, deliberately —
                 * a nil-RCV annuity really does leave nothing behind), so the nil version lost the legacy
                 * ranking by its full purchase price on day one and could never win a comparison it was
                 * excluded from. Same fix as the Aged Care engine.
                 */
                if (annuityResidual !== 'nil' && annuityResidual !== 'guaranteed') candidates.push({
                    id: 'rv-sell-home-annuity-rcv',
                    name: `Sell your home, buy a lifetime annuity (${sizeWords}, capital returned)${restWords}`,
                    description: `The same annuity, but with the purchase price coming back to the estate on an early death — in full within the first half of life expectancy, less after that — rather than kept by the provider. It pays less income, and Centrelink still counts only 60% either way${annuityRest > 0 ? ' — with the rest invested in shares / managed funds' : ''}.`,
                    fundFrom: 'sell_home',
                    sellHomeDetails: {
                        salePrice: homeValue, sellingCosts,
                        downsizerContribution: 0, partnerDownsizerContribution: 0,
                        commenceIncomeStream: false, giftingAmount: 0,
                        funeralBondPurchase: 0, purchaseInvestmentProperty: 0,
                        purchaseShares: annuityRest,
                        purchaseAnnuity: annuityAmount, rvAnnuityType: 'lifetime',
                        rvAnnuityResidualPercent: 100,
                    },
                });

                if (offerGuaranteed) candidates.push({
                    id: 'rv-sell-home-annuity-guaranteed',
                    name: `Sell your home, buy a lifetime annuity (${sizeWords}, full amount guaranteed)${restWords}`,
                    description: `A lifetime annuity and a life policy bought together, so the full purchase price comes back to the estate whenever death comes. It pays the least income of the three, and only the annuity half earns the 60% assets-test treatment; the policy is counted at what it cost${annuityRest > 0 ? ' — with the rest invested in shares / managed funds' : ''}.`,
                    fundFrom: 'sell_home',
                    sellHomeDetails: {
                        salePrice: homeValue, sellingCosts,
                        downsizerContribution: 0, partnerDownsizerContribution: 0,
                        commenceIncomeStream: false, giftingAmount: 0,
                        funeralBondPurchase: 0, purchaseInvestmentProperty: 0,
                        purchaseShares: annuityRest,
                        purchaseAnnuity: annuityAmount, rvAnnuityType: 'guaranteed',
                        rvAnnuityResidualPercent: 100,
                    },
                });
            }
        }

        // 7. Sell the home and top up super via the downsizer contribution (if age-eligible).
        if (downsizerEligible) {
            const netAfterEntry = Math.max(0, homeValue - sellingCosts - entryFee);
            // Age-aware: fills the member whose accumulation super is exempt from the means tests
            // (under Age Pension age) FIRST. Where neither or both are exempt this is the same even
            // split it always was — see allocateDownsizerContribution.
            const allocation = allocateDownsizerContribution(factFind as unknown as FactFind, netAfterEntry);
            const downsizer = allocation.client;
            const partnerDownsizer = allocation.partner;
            const preferredName =
                allocation.preferred === 'partner'
                    ? factFind?.partner?.firstName || 'your partner'
                    : allocation.preferred === 'client'
                      ? factFind?.client?.firstName || 'you'
                      : null;
            const preferenceNote = preferredName
                ? ` Weighted to ${preferredName}'s account first: while they are under Age Pension age, super held in their name is not counted in the assets or income tests.`
                : '';
            if (downsizer > 0) {
                candidates.push({
                    id: 'rv-sell-home-downsizer',
                    name: 'Sell your home and top up super',
                    description: `Sell the home, pay the entry contribution, then move freed-up money into super using the downsizer contribution (up to $300,000 each) — often more tax-effective and pension-friendly.${preferenceNote}`,
                    fundFrom: 'sell_home',
                    sellHomeDetails: {
                        salePrice: homeValue,
                        sellingCosts,
                        downsizerContribution: downsizer,
                        partnerDownsizerContribution: partnerDownsizer,
                        commenceIncomeStream: false,
                        giftingAmount: 0,
                        funeralBondPurchase: 0,
                        purchaseAnnuity: 0,
                        purchaseInvestmentProperty: 0,
                        purchaseShares: 0,
                    },
                });

                // 7a. The same downsizer contribution, then STARTED as an account-based pension
                //     rather than left in accumulation. For a client already over preservation age
                //     this is usually the point of the exercise — earnings become tax free and the
                //     money is drawable — and it is a genuinely different projection, so it is
                //     ranked rather than assumed.
                candidates.push({
                    id: 'rv-sell-home-downsizer-abp',
                    name: 'Sell your home, top up super and start a pension from it',
                    description: 'Sell the home, pay the entry contribution, move freed-up money into super using the downsizer contribution, then commence an account-based pension from it — investment earnings become tax free and the money is drawable as income, rather than sitting in accumulation phase.',
                    fundFrom: 'sell_home',
                    sellHomeDetails: {
                        salePrice: homeValue,
                        sellingCosts,
                        downsizerContribution: downsizer,
                        partnerDownsizerContribution: partnerDownsizer,
                        commenceIncomeStream: true,
                        giftingAmount: 0,
                        funeralBondPurchase: 0,
                        purchaseAnnuity: 0,
                        purchaseInvestmentProperty: 0,
                        purchaseShares: 0,
                    },
                });

                // 7b. Downsizer to the caps commenced as an ABP, and the remainder INVESTED in
                //     shares / managed funds rather than left in cash. Without this candidate the
                //     downsizer family is not comparable with "sell and invest": the plain
                //     downsizer strategies leave everything beyond the caps in a 2%-interest bank
                //     account, so they lose to a full share portfolio on asset allocation alone and
                //     the ranking says nothing about the super wrapper. This is the true
                //     apples-to-apples: the same portfolio, split between the pension wrapper and
                //     personal names — the tax treatment decides.
                const remainderAfterDownsizerAll = Math.max(0, netAfterEntry - downsizer - partnerDownsizer);
                if (remainderAfterDownsizerAll >= 50000) {
                    candidates.push({
                        id: 'rv-sell-home-downsizer-abp-invest',
                        name: 'Sell your home, start a super pension and invest the rest',
                        description: 'Sell the home, pay the entry contribution, move what the downsizer caps allow into super and commence an account-based pension from it, and invest the remainder in shares / managed funds — the same growth portfolio as investing it all personally, but with part of it sheltered inside the pension\'s tax-free wrapper.',
                        fundFrom: 'sell_home',
                        sellHomeDetails: {
                            salePrice: homeValue,
                            sellingCosts,
                            downsizerContribution: downsizer,
                            partnerDownsizerContribution: partnerDownsizer,
                            commenceIncomeStream: true,
                            giftingAmount: 0,
                            funeralBondPurchase: 0,
                            purchaseAnnuity: 0,
                            purchaseInvestmentProperty: 0,
                            purchaseShares: remainderAfterDownsizerAll,
                        },
                    });
                }

                // 7b-ii. The same split, but the remainder buys an INVESTMENT PROPERTY rather than
                //     shares (owner-caught, 2026-08-26: "it never ran a scenario where it sold the
                //     home, put it in super, started a pension and then bought an investment
                //     property with an assumed rental return"). The field already paired the super
                //     pension with a share portfolio, and paired an investment property with a plain
                //     sale — but never the two together, which is a combination advisers genuinely
                //     recommend: the tax-free pension wrapper for what fits the caps, and a rental
                //     income stream for the rest. Rent and expenses use the same assumed yield the
                //     stand-alone investment-property candidate uses, so the two are comparable.
                if (remainderAfterDownsizerAll >= 50000) {
                    candidates.push({
                        id: 'rv-sell-home-downsizer-abp-ip',
                        name: 'Sell your home, start a super pension and buy an investment property',
                        description: 'Sell the home, pay the entry contribution, move what the downsizer caps allow into super and commence an account-based pension from it, then buy an investment property with the remainder — a tax-free pension wrapper alongside rental income. The property is an assessable asset and its net rent counts for the pension, and it is far less flexible than shares if money is needed quickly.',
                        fundFrom: 'sell_home',
                        sellHomeDetails: {
                            salePrice: homeValue,
                            sellingCosts,
                            downsizerContribution: downsizer,
                            partnerDownsizerContribution: partnerDownsizer,
                            commenceIncomeStream: true,
                            giftingAmount: 0,
                            funeralBondPurchase: 0,
                            purchaseAnnuity: 0,
                            ...propertyFor(remainderAfterDownsizerAll),
                            purchaseShares: 0,
                        },
                    });
                }

                // 7c. Downsizer to the caps, and a lifetime annuity with what's left — the two
                //     pension-friendly conversions combined, when meaningful money remains beyond
                //     the super caps. The annuity is sized to the setup's share cap (of the whole
                //     amount freed, as for candidate 6) and the rest invested, so the combination
                //     is comparable with the stand-alone annuity rather than an all-in outlier.
                //     (7d below is the same with the downsizer commenced as an ABP.)
                const remainderAfterDownsizer = Math.max(0, netAfterEntry - downsizer - partnerDownsizer);
                const downsizerAnnuity = Math.min(remainderAfterDownsizer, Math.round(netAfterEntry * (annuityCapPct / 100)));
                if (remainderAfterDownsizer >= 50000 && downsizerAnnuity >= 25000) {
                    const downsizerAnnuityRest = Math.max(0, remainderAfterDownsizer - downsizerAnnuity);
                    candidates.push({
                        id: 'rv-sell-home-downsizer-annuity',
                        name: 'Sell your home, top up super and buy an annuity',
                        description: `Sell the home, pay the entry contribution, move what the downsizer caps allow into super, and put ${formatRoughAmount(downsizerAnnuity)} of the remainder into a lifetime annuity — combining the super tax shelter with guaranteed income and the annuity's 60% assets-test discount${downsizerAnnuityRest > 0 ? ', the rest invested in shares / managed funds' : ''}.`,
                        fundFrom: 'sell_home',
                        sellHomeDetails: {
                            salePrice: homeValue,
                            sellingCosts,
                            downsizerContribution: downsizer,
                            partnerDownsizerContribution: partnerDownsizer,
                            commenceIncomeStream: false,
                            giftingAmount: 0,
                            funeralBondPurchase: 0,
                            purchaseAnnuity: downsizerAnnuity,
                            rvAnnuityType: 'lifetime',
                            rvAnnuityResidualPercent: annuityResidual === 'returned' ? 100 : 0,
                            purchaseInvestmentProperty: 0,
                            purchaseShares: downsizerAnnuityRest,
                        },
                    });

                    // 7d. The same combination with the downsizer money STARTED as an account-based
                    //     pension — a guaranteed income floor from the annuity plus a tax-free,
                    //     drawable pension beside it. This is the shape many real adviser
                    //     recommendations take, so it is ranked rather than left for the adviser to
                    //     assemble by hand.
                    candidates.push({
                        id: 'rv-sell-home-downsizer-abp-annuity',
                        name: 'Sell your home, start a super pension and buy an annuity',
                        description: `Sell the home, pay the entry contribution, move what the downsizer caps allow into super and commence an account-based pension from it, and put ${formatRoughAmount(downsizerAnnuity)} of the remainder into a lifetime annuity — guaranteed income for life with its 60% assets-test discount, plus tax-free investment earnings and drawable income from the pension${downsizerAnnuityRest > 0 ? ', the rest invested in shares / managed funds' : ''}.`,
                        fundFrom: 'sell_home',
                        sellHomeDetails: {
                            salePrice: homeValue,
                            sellingCosts,
                            downsizerContribution: downsizer,
                            partnerDownsizerContribution: partnerDownsizer,
                            commenceIncomeStream: true,
                            giftingAmount: 0,
                            funeralBondPurchase: 0,
                            purchaseAnnuity: downsizerAnnuity,
                            rvAnnuityType: 'lifetime',
                            rvAnnuityResidualPercent: annuityResidual === 'returned' ? 100 : 0,
                            purchaseInvestmentProperty: 0,
                            purchaseShares: downsizerAnnuityRest,
                        },
                    });
                }
            }
        }
    }

    // 8. Sell an investment property to fund the move (if one exists).
    if (hasIp) {
        const ip = investmentProps[0];
        candidates.push({
            id: 'rv-sell-ip',
            name: 'Sell an investment property',
            description: 'Sell an investment property and use the net proceeds (after selling costs and CGT) for the entry contribution.',
            fundFrom: 'investment_property',
            investmentPropertyDetails: {
                selectedPropertyId: ip?.id ?? null,
                entryFeeAllocation: entryFee,
                downsizerContribution: 0,
                partnerDownsizerContribution: 0,
                commenceIncomeStream: false,
                giftingAmount: 0,
                funeralBondPurchase: 0,
            },
        });
    }

    /*
     * 8b. THE TWO MEANS-TEST LEVERS THE ENGINE HARD-CODED TO ZERO.
     *
     * Every candidate above sets `giftingAmount: 0` and `funeralBondPurchase: 0`. Both are
     * first-class Manual Control inputs and both act on the problem this module actually creates: a
     * village entry above the extra allowable amount makes the resident a HOMEOWNER, and whatever is
     * left of the sale proceeds is counted in full. A funeral bond takes part of that back outside
     * the assets test; a gift within the free area takes it out altogether.
     *
     * Offered only on the sell-home paths, because they are the ones that produce a residual to
     * allocate. A liquid-funded entry has already spent the savings on the contribution.
     *
     * Gifting stays INSIDE the yearly free area. Above it the excess is still counted, and still
     * deemed to earn income, for five years under the deprivation rules — which this module now
     * models correctly, and which is a real cost the engine cannot weigh against a family's own
     * reasons for a larger gift.
     */
    if (hasHome && entryFee > 0) {
        // The same 2.5% agent/legal estimate the sell-home candidates above use.
        const sellingCosts = Math.round(homeValue * 0.025);
        const residualAfterEntry = Math.max(0, homeValue - sellingCosts - entryFee);
        const isCouple = factFind?.adviceFor === 'couple';
        const baseSellDetails = {
            salePrice: homeValue, sellingCosts,
            downsizerContribution: 0, partnerDownsizerContribution: 0,
            commenceIncomeStream: false, giftingAmount: 0,
            funeralBondPurchase: 0, purchaseAnnuity: 0,
            purchaseInvestmentProperty: 0, purchaseShares: 0,
        };

        const bondAmount = Math.min(residualAfterEntry, FUNERAL_BOND_LIMIT * (isCouple ? 2 : 1));
        if (bondAmount > 0) {
            candidates.push({
                id: 'rv-sell-home-funeral-bond',
                name: 'Sell your home and shelter part of the rest in a funeral bond',
                description: `Sell the home, pay the entry contribution, and put ${formatCurrency(bondAmount)} of what is left into a funeral bond${isCouple ? ' each' : ''} — exempt from the assets test up to ${formatCurrency(FUNERAL_BOND_LIMIT)} per person, so that part stops being counted. The remainder is invested.`,
                fundFrom: 'sell_home',
                sellHomeDetails: {
                    ...baseSellDetails,
                    funeralBondPurchase: bondAmount,
                    purchaseShares: Math.max(0, residualAfterEntry - bondAmount),
                },
            });
        }

        const giftAmount = Math.min(residualAfterEntry, GIFTING_LIMIT_ANNUAL);
        if (giftAmount > 0) {
            candidates.push({
                id: 'rv-sell-home-gift',
                name: 'Sell your home and gift within the free area',
                description: `Sell the home, pay the entry contribution, and gift ${formatCurrency(giftAmount)} of what is left to family. Gifts up to ${formatCurrency(GIFTING_LIMIT_ANNUAL)} a year leave the means test straight away; anything above that is still counted, and still deemed, for five years — so this stays inside the free area. The remainder is invested.`,
                fundFrom: 'sell_home',
                sellHomeDetails: {
                    ...baseSellDetails,
                    giftingAmount: giftAmount,
                    purchaseShares: Math.max(0, residualAfterEntry - giftAmount),
                },
            });
        }
    }

    // 9. Debt clearance — an orthogonal decision PAIRED against every move strategy above, the same
    //    pattern the aged care engine uses. Interest stops, the repayments leave the cashflow and
    //    assessable assets fall — but the money spent is money no other part of the plan can use,
    //    which is exactly the competition worth ranking rather than deciding by instinct. The engine
    //    draws the payoff from liquid assets in its own order, after the entry fee's own draw (the
    //    shared pool means a dollar cannot fund both). The stay-home baseline is left pure: it must
    //    stay indistinguishable from the client's life as it is, or nothing has a benchmark.
    //    (Aged care pairs only a spread of its RAD levels because its base set runs to hundreds;
    //    this base set is a dozen, so every move strategy gets its twin.)
    const debts = payableDebtsOf(factFind);
    const totalDebt = debts.reduce((sum, d) => sum + d.balance, 0);
    // Below $5,000 a payoff cannot move a ranking (the assets-test taper on $5,000 is ~$390 a year)
    // and each twin costs a full projection — the same materiality floor as the aged care engine.
    if (totalDebt >= 5_000) {
        const payments = Object.fromEntries(debts.map((d) => [d.key, d.balance]));
        const twins = candidates
            .filter((c) => c.stayHome !== true)
            .map((c) => ({
                ...c,
                id: `${c.id}-clear-debt`,
                name: `${c.name} + clear debts (${formatRoughAmount(totalDebt)})`,
                description: `${c.description} This version also clears your outstanding debts from liquid assets at the same time — the interest stops and the repayments leave your cashflow, but the money spent is money the rest of the plan cannot use.`,
                debtPayoff: {
                    payments,
                    repaymentBasis: 'keep_repayment' as const,
                },
            }));
        candidates.push(...twins);
    }

    /*
     * FINAL GATE — nothing is offered that cannot pay the whole entry contribution.
     *
     * The liquid paths are already gated where they are generated (per variant, since one draw order
     * can fund where another falls short). This catches the PROPERTY paths, and expresses the rule
     * once for anything added later: a village ingoing contribution is a contractual price with no
     * right to part-pay it, so a plan that raises less than the full amount is not a strategy.
     *
     * Deliberately conservative on the investment-property path: it is measured on the GROSS value,
     * which can only over-state what the sale nets after costs and CGT. That direction is the safe
     * one — a candidate that grosses enough but nets slightly short is no longer able to flatter
     * itself, because the engine now strikes the exit entitlement on what was actually PAID
     * (VillageContract.entryFeePaid), so it simply ranks on its true, worse numbers instead of
     * winning on a phantom.
     */
    if (entryFee > 0) {
        const netSaleProceeds = hasHome ? homeValue - Math.round(homeValue * 0.025) : 0;
        const grossIpValue = hasIp ? investmentProps.reduce((max, p) => Math.max(max, parseFloat(String(p?.value ?? '')) || 0), 0) : 0;
        return candidates.filter((candidate) => {
            if (candidate.stayHome === true) return true;
            if (candidate.fundFrom === 'sell_home') return netSaleProceeds >= entryFee - 1;
            if (candidate.fundFrom === 'investment_property') return grossIpValue >= entryFee - 1;
            return true;
        });
    }

    return candidates;
}

/**
 * Evaluate every candidate with the RV projection engine and rank into goal leaders.
 * @param villageInputs - entry fee, ongoing fees, DMF, growth, capital-gain share, residency years…
 */
export async function buildRetirementVillageSuggestions(
    factFind: DraftFactFind,
    villageInputs: RetirementVillageInputs,
    options: RetirementVillageSuggestionsOptions = {},
): Promise<RetirementVillageSuggestionsResult> {
    const legacyYears = options.legacyYears || 5;
    let candidates = generateRetirementVillageCandidates(factFind, villageInputs, options.setup || {});

    // A quoted annuity income replaces the indicative rate everywhere it applies, BEFORE evaluation,
    // so the whole field is ranked on the same figure the client was actually offered.
    const quotedAnnuityIncome = options.annuityIncomeOverride;
    if (quotedAnnuityIncome != null && Number.isFinite(quotedAnnuityIncome) && quotedAnnuityIncome > 0) {
        candidates = candidates.map((candidate) => {
            const details = candidate.sellHomeDetails;
            if (!details || !((details.purchaseAnnuity ?? 0) > 0)) return candidate;
            return { ...candidate, sellHomeDetails: { ...details, rvAnnuityIncomeOverride: quotedAnnuityIncome } };
        });
    }

    // One request evaluates every candidate against the engine server-side (see
    // server/src/rest/calculation/suggestionEngine/retirementVillage.ts) instead of one request per candidate.
    // The village inputs carry the payout choice to the server, where the moved balance sheet is built.
    const inputsForEngine = { ...villageInputs, oldUnitPaysAtMove: options.setup?.oldUnitPaysAtMove !== false };
    const strategies: EvaluatedStrategy[] = await calculationApi.evaluateRetirementVillageCandidates(factFind, inputsForEngine, candidates, legacyYears, options.villageEntryDate);

    // The stay-home evaluation is the do-nothing BASELINE: goal leaders are ranked among the real
    // move strategies, each measured against it — the same benchmark discipline as the other engines.
    const stayHome = strategies.find((s) => s.id === 'rv-stay-home') || null;
    const moveStrategies = strategies.filter((s) => s.id !== 'rv-stay-home');
    const recommended = buildRecommendedOptimalStrategies(moveStrategies, stayHome, { cashflowTarget: Number(options.setup?.cashflowTarget) || 0 });
    return { strategies, recommended, stayHome };
}
